/**
 * ProShip Direct v2 — My Couriers.
 *
 * The merchant's own courier accounts and nothing else. ProShip's couriers are deliberately not
 * shown here as a second panel: they need no configuration from the merchant, only an on/off
 * decision, which lives in Couriers. A panel shaped like this one, holding 114 codes with nothing
 * to do to them, is the screen the reviewed build ended up with.
 *
 * The five configuration columns are provenance, not readiness — a tick is yours, an inherit
 * arrow is ours, and neither is an error. Two things genuinely block, and both are loud:
 * credentials the carrier rejects, and a missing rate card, which leaves the account out of every
 * cost comparison.
 */
import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Grid, Typography, Button, Switch, Tooltip, IconButton, Chip,
} from '@mui/material';
import Loader from '../../../common/Loader';
import { Link } from 'react-router-dom';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import withLayout from '../../../hocs/Layout';
import PageTitle from '../../../common/PageTitle';
import ProzoDataGrid from '../../../common/ProzoDataGrid';
import ConfirmDialog from '../../../common/ConfirmDialog';
import { cellStackSx, primaryTextSx, secondaryTextSx } from '../../orders/list2/cellStyles';
import { pageContainerSx, primaryBtnSx, dataGridWrapSx } from '../../../styles/patterns';
import { SUBTLE, NAVY, ERROR, SUCCESS, WARNING, LIGHT_BG } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { GAP } from '../../../styles/foundation/spacing';
import { formatDate } from '../../../helpers/UtilityHelper';
import { useSnackbar } from 'notistack';

import { usePlan, useDirectCourierSetup, countsAgainstPlan } from '../data/hooks';
import { SETUP_DIMENSIONS, CREDENTIAL_STATE } from '../data/defaults';
import { dpPath } from '../flags';
import { ProvenanceCell, CredentialChip } from '../components/chips';
import { PlanInactive, NoAccounts } from '../components/states';
import { statCardSx, statLabelSx, statValueSx, statSubSx } from '../components/stats';
import CourierEditDrawer from '../components/CourierEditDrawer';


/** One number and what it means. Four of these answer "is my Direct setup working?". */
function StatCard({ label, value, sub, tone }) {
  return (
    <Box sx={statCardSx}>
      <Typography sx={statLabelSx}>{label}</Typography>
      <Typography sx={{ ...statValueSx, color: tone || NAVY }}>{value}</Typography>
      <Typography sx={statSubSx}>{sub}</Typography>
    </Box>
  );
}

/** Setup pages are per account, so every link out of a row names the account it came from. */
/*
  Courier Setup is one page with a tab per dimension, so a link names both: which account, and
  which of its five settings. Landing on the page and making the merchant find the row they just
  clicked is the thing the tabs were meant to stop.
*/
const setupPath = (route, accountId) =>
  `${dpPath('/courier-setup')}?account=${accountId}&tab=${route}`;


function MyCouriers() {
  const { enqueueSnackbar } = useSnackbar();
  const { plan, usage } = usePlan();


  /*
    THE LIST IS NOW REAL. Accounts come from GET direct/couriers — the ProShip routing documents
    written when the merchant registered, which is what allocation actually ships on. The old
    localStorage list could show an account that existed nowhere.

    The SETUP columns (serviceability, zones, TAT, rate card) still read `config`, which no
    endpoint serves yet, so a freshly registered courier shows those as "not provided". That is
    honest rather than wrong — the merchant genuinely has not uploaded them — and it resolves
    when the setup endpoints land.

    Retired accounts still come from the store: retirement has no endpoint either, so nothing
    can be retired through this screen yet (the button is disabled below).
  */
  const direct = useDirectCourierSetup();
  const carrierNameOf = React.useMemo(
    () => new Map(direct.parents.map((p) => [p.courierCode, p.courierName || p.courierCode])),
    [direct.parents]
  );
  const couriers = React.useMemo(
    () => direct.accounts.map((a) => ({
      id: a.prozoId,
      prozoId: a.prozoId,
      /* The carrier's name — displayName is just the account code; see useDirectAccounts. */
      carrierName: carrierNameOf.get(a.courierCode) || a.courierCode,
      courierCode: a.courierCode,
      direction: a.direction,
      cpId: a.cpId,
      accountCode: a.accountCode,
      // Direction and mode are separate answers now; the column wants one label.
      serviceType: [a.direction, a.mode].filter(Boolean).join(' · ') || '—',
      credentials: a.credentialState || CREDENTIAL_STATE.FAILED,
      enabled: a.isActive !== false,
      lastError: a.lastError || null,
      lastTestedAt: a.lastTestedAt || null,
      config: {},            // no endpoint yet — see note above
      retired: a.retired === true,
      registeredAt: a.registeredAt,
      registeredBy: a.registeredBy,
    })),
    [direct.accounts, carrierNameOf]
  );
  /*
    The edited account's credential fields, from the server — the same list the connect screen
    renders and the same list the backend validates against. Loaded when a row is opened rather
    than up front: it is one request per carrier and most sessions never open the drawer.
  */
  const [credFields, setCredFields] = useState([]);
  const [schemaError, setSchemaError] = useState(null);
  /* What the merchant entered for the edited account, so the drawer opens filled in. */
  const [detail, setDetail] = useState(null);
  const [testingRow, setTestingRow] = useState(null);
  const [togglingRow, setTogglingRow] = useState(null);

  const [page, setPage] = useState(0);
  const [sizePerPage, setSizePerPage] = useState(10);
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const [editing, setEditing] = useState(null);

  /* Fetch the edited carrier's fields; clear them when the drawer closes so the next row cannot
     briefly render the previous carrier's form. */
  useEffect(() => {
    if (!editing) { setCredFields([]); setSchemaError(null); setDetail(null); return undefined; }
    let alive = true;
    setCredFields([]);
    setSchemaError(null);
    setDetail(null);
    Promise.all([
      direct.loadSchema(editing.courierCode, editing.direction),
      direct.loadAccount(editing.accountCode),
    ])
      .then(([f, d]) => { if (alive) { setCredFields(f); setDetail(d || {}); } })
      .catch((e) => { if (alive) setSchemaError(e.message); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  /**
   * Pause or resume a courier.
   *
   * Re-enabling can be REFUSED — the plan's courier allowance is re-checked on the way back on,
   * or a merchant on a three-courier plan could register ten and rotate through them. The
   * server's sentence is shown as-is, because "your plan allows 3 and you have 3" is something
   * the merchant can act on.
   */
  const toggleActive = async (row) => {
    setTogglingRow(row.accountCode);
    try {
      const res = await direct.setActive(row.accountCode, !row.enabled);
      enqueueSnackbar(res?.message || 'Updated.', { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(e.message, { variant: 'error' });
    } finally {
      setTogglingRow(null);
    }
  };

  /**
   * Re-run the connection test on a saved account, and record it.
   *
   * The merchant's half of a credential problem usually happens at the carrier — a key reissued,
   * an account reactivated — with nothing to change on this screen. Without a retest they would
   * have to re-enter credentials they have not changed just to clear a stale FAILED.
   */
  const retest = async (row) => {
    setTestingRow(row.accountCode);
    try {
      const res = await direct.testAccount(row.accountCode);
      enqueueSnackbar(
        res?.success ? `${row.carrierName} connection verified.` : res?.message || 'The connection test failed.',
        { variant: res?.success ? 'success' : 'error' }
      );
    } catch (e) {
      enqueueSnackbar(e.message, { variant: 'error' });
    } finally {
      setTestingRow(null);
    }
  };

  const allowed = usage.plan?.courierIntegrations || 0;
  const live = couriers.filter((c) => c.enabled && c.credentials === CREDENTIAL_STATE.TESTED).length;
  const failing = couriers.filter((c) => c.credentials === CREDENTIAL_STATE.FAILED).length;
  /*
    Always 0 now, and kept only so the card below reads the same for a legacy row. A courier is
    registered ONLY after a passing probe and every test since is recorded, so the server returns
    TESTED or FAILED and never PENDING. Left in place rather than ripped out because a document
    written before the probe existed still reports FAILED, not PENDING, and this counter is the
    one thing that would quietly change meaning if PENDING ever came back.
  */
  const untested = couriers.filter((c) => c.credentials === CREDENTIAL_STATE.PENDING).length;
  const withRates = couriers.filter((c) => c.config?.rateCard === 'OWN').length;
  /* The plan caps ACTIVE couriers; switched-off and retired ones cost nothing. See countsAgainstPlan. */
  const counted = couriers.filter(countsAgainstPlan).length;
  const atLimit = allowed > 0 && counted >= allowed;

  /*
    Live accounts first, retired after: the merchant needs to see a retired account exists (a
    shipment or an invoice can still point at it) without it competing for attention.

    Both lists are the SAME server response now, split on the tombstone. They used to be the
    server list plus whatever the browser's own store happened to hold, which meant a retired
    courier appeared on the machine that retired it and nowhere else.
  */
  const rows = [
    ...couriers.filter((c) => !c.retired),
    ...couriers.filter((c) => c.retired),
  ];

  const columns = [
    {
      field: 'carrierName',
      headerName: 'Carrier',
      minWidth: 200,
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={cellStackSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ ...primaryTextSx, color: row.retired ? SUBTLE : undefined }}>
              {row.carrierName}
            </Typography>
            {row.retired && (
              <Tooltip
                title={`Retired ${row.retiredAt ? formatDate(row.retiredAt, 'dd MMM yyyy') : ''} by ${row.retiredBy || '—'}. Kept because shipments already sent on it point here.`}
              >
                <Chip
                  size='small'
                  label='Retired'
                  sx={{
                    fontFamily: FONT,
                    fontSize: SIZE['4xs'],
                    fontWeight: WEIGHT.semibold,
                    height: 19,
                    borderRadius: '4px',
                    bgcolor: LIGHT_BG,
                    color: SUBTLE,
                  }}
                />
              </Tooltip>
            )}
          </Box>
          <Typography sx={secondaryTextSx}>{row.accountCode}</Typography>
        </Box>
      ),
    },
    {
      field: 'serviceType',
      headerName: 'Service',
      minWidth: 85,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={cellStackSx}>
          <Typography sx={secondaryTextSx}>{row.serviceType}</Typography>
        </Box>
      ),
    },
    {
      // Credential state plus, when the carrier has rejected us, what it actually said. A merchant
      // cannot fix "Failed"; they can fix "the password was rejected".
      field: 'credentials',
      headerName: 'Connection',
      minWidth: 150,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ ...cellStackSx, gap: 0.4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
            <CredentialChip state={row.credentials} />
            {row.lastError && (
              <Tooltip title={row.lastError}>
                <ErrorOutlineIcon sx={{ fontSize: 15, color: ERROR }} />
              </Tooltip>
            )}
          </Box>
          {row.lastError && (
            <Typography
              onClick={() => setEditing(row)}
              sx={{
                fontFamily: FONT,
                fontSize: SIZE['4xs'],
                color: ERROR,
                fontWeight: WEIGHT.semibold,
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              Fix credentials
            </Typography>
          )}
          {/*
            RETEST, NOT RE-ENTER. Most credential failures are fixed at the carrier — a key
            reissued, an account reactivated — leaving nothing to change here. Without this the
            merchant would have to retype credentials they never changed just to clear a stale
            Failed, and a passing account would have no way to prove it still works.
          */}
          <Typography
            onClick={() => testingRow !== row.accountCode && retest(row)}
            sx={{
              fontFamily: FONT,
              fontSize: SIZE['4xs'],
              color: SUBTLE,
              cursor: testingRow === row.accountCode ? 'default' : 'pointer',
              textDecoration: 'underline',
            }}
          >
            {testingRow === row.accountCode ? 'Testing…' : 'Re-test'}
          </Typography>
          {row.lastTestedAt && (
            <Typography sx={{ fontFamily: FONT, fontSize: SIZE['4xs'], color: SUBTLE }}>
              {formatDate(row.lastTestedAt, 'DD MMM, hh:mm A')}
            </Typography>
          )}
        </Box>
      ),
    },
    ...SETUP_DIMENSIONS.map((d) => ({
      field: d.key,
      /*
        The SHORT label heads these columns. “Serviceability” and “Zone Mapping” are wider than the
        column they sit over and were truncating to “Serviceabili…”, which reads as a rendering
        fault. `short` exists for exactly this, and using it here matches the same four labels on
        the Home courier rows.
      */
      headerName: d.short || d.label,
      minWidth: d.required ? 115 : 105,
      sortable: false,
      renderCell: ({ row }) => {
        const provided = row.config?.[d.key] === 'OWN';
        // The rate card has no ProShip default to inherit — a blank one is a gap, not a choice.
        if (d.required && !provided) {
          return (
            <Box sx={cellStackSx}>
              <Tooltip title='No rate card uploaded. Direct cost and the Direct vs ProShip comparison stay blank for this account, and cheapest-first allocation skips it.'>
                <Chip
                  size='small'
                  component={Link}
                  to={setupPath(d.route, row.id)}
                  clickable
                  label='Upload'
                  sx={{
                    fontFamily: FONT,
                    fontSize: SIZE['4xs'],
                    fontWeight: WEIGHT.semibold,
                    height: 20,
                    borderRadius: '4px',
                    color: WARNING,
                    bgcolor: `${WARNING}1f`,
                  }}
                />
              </Tooltip>
            </Box>
          );
        }
        return (
          <Box sx={cellStackSx}>
            <ProvenanceCell provided={provided} label={d.label} to={setupPath(d.route, row.id)} />
          </Box>
        );
      },
    })),
    {
      field: 'enabled',
      headerName: 'Enabled',
      minWidth: 90,
      sortable: false,
      renderCell: ({ row }) => {
        const blocked = row.credentials !== CREDENTIAL_STATE.TESTED;
        return (
          <Tooltip
            title={
              row.retired
                ? 'Retired accounts stay off permanently. Connect the contract again to use it.'
                : blocked
                  ? 'Credentials must pass before this account can be used'
                  : ''
            }
          >
            <Box sx={cellStackSx}>
              {/*
                Real now: flips isActive on the routing document, which is what allocation
                filters on. The PCIS account is deliberately left alive — PCIS refuses every
                operation on an inactive account, tracking included, so switching it off there
                would blind us to the shipments already in flight on this courier.
              */}
              <Switch
                size='small'
                checked={row.enabled && !blocked && !row.retired}
                disabled={blocked || row.retired || togglingRow === row.accountCode}
                onChange={() => toggleActive(row)}
              />
            </Box>
          </Tooltip>
        );
      },
    },
    {
      /*
        The Actions column is back, because the endpoints exist now. It was pulled when these
        rows became real registered accounts and edit/retire were still store-only writes — a
        control that silently edited localStorage would have misrepresented a live courier.

        The per-dimension setup links stay out of it: those uploads are the next piece of work,
        and a menu item leading to a screen that cannot save is the same problem in a new place.
      */
      field: 'actions',
      headerName: '',
      minWidth: 90,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title={row.retired ? 'Retired accounts cannot be edited' : 'Replace credentials and contract limits'}>
            <span>
              <IconButton
                size='small'
                disabled={row.retired}
                onClick={() => setEditing(row)}
                aria-label='edit account'
              >
                <EditOutlinedIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={row.retired ? 'Already retired' : 'Retire this account'}>
            <span>
              <IconButton
                size='small'
                disabled={row.retired}
                onClick={() => setPendingRemoval(row)}
                aria-label='retire account'
              >
                <DeleteOutlineIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const body = () => {
    if (!plan.active) return <PlanInactive what='Courier management' />;
    /*
      Loading and failure are now distinguishable from "none connected". Before, a failed fetch
      and an empty account list both rendered NoAccounts — which would have told a merchant with
      five live couriers that they had none.
    */
    if (direct.loading) return <Loader />;
    if (direct.error) return <Alert severity='warning'>{direct.error}</Alert>;
    if (!couriers.length) return <NoAccounts />;

    return (
      <>
        <Grid container spacing={1.5}>
          <Grid item xs={6} md={3}>
            <StatCard
              label='Accounts connected'
              value={allowed ? `${counted} / ${allowed}` : couriers.length}
              sub={allowed ? `included in the ${usage.plan?.name} plan` : 'courier contracts you hold'}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard
              label='Live for allocation'
              value={live}
              tone={live ? SUCCESS : SUBTLE}
              sub='tested and switched on'
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard
              label='Needs attention'
              value={failing + untested}
              tone={failing ? ERROR : untested ? WARNING : SUBTLE}
              sub={
                failing
                  ? `${failing} rejected by the carrier${untested ? `, ${untested} untested` : ''}`
                  : untested
                    ? `${untested} never tested`
                    : 'every account is authenticating'
              }
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard
              label='Rate cards uploaded'
              value={`${withRates} / ${couriers.length}`}
              tone={withRates === couriers.length ? SUCCESS : WARNING}
              sub='needed to compare Direct cost with ProShip'
            />
          </Grid>
        </Grid>

        <Box sx={dataGridWrapSx}>
          <ProzoDataGrid
            columns={columns}
            rows={rows.slice(page * sizePerPage, page * sizePerPage + sizePerPage)}
            rowCount={rows.length}
            page={page}
            setPage={setPage}
            sizePerPage={sizePerPage}
            setSizePerPage={setSizePerPage}
            paginationMode='client'
            getRowId={(row) => row.id}
            disableColumnMenu
            disableColumnFilter
          />
        </Box>
      </>
    );
  };

  return (
    <Box sx={pageContainerSx}>
      <PageTitle subTitle='Courier contracts you hold yourself. Anything you have not configured runs on ProShip defaults — except the rate card, which only you have.'>
        My Couriers
      </PageTitle>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: GAP.lg, color: NAVY }}>
        {plan.active && couriers.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.md, flexWrap: 'wrap' }}>
            <Tooltip
              title={atLimit ? `The ${usage.plan?.name} plan allows ${allowed} active couriers. Switch one off, retire it, or upgrade to connect another.` : ''}
            >
              <span>
                <Button
                  component={Link}
                  to={dpPath('/couriers/connect')}
                  variant='contained'
                  size='small'
                  disabled={atLimit}
                  startIcon={<AddOutlinedIcon />}
                  sx={primaryBtnSx}
                >
                  Connect a courier
                </Button>
              </span>
            </Tooltip>
          </Box>
        )}

        {body()}
      </Box>

      <CourierEditDrawer
        account={editing}
        open={Boolean(editing)}
        credFields={credFields}
        detail={detail}
        schemaError={schemaError}
        onClose={() => setEditing(null)}
        /*
          The real thing: the server tests the new credentials against the carrier and writes them
          to PCIS only if they pass. This used to be the localStorage store's updateCourier, so
          the screen said "saved" while PCIS kept the credentials that were failing.
        */
        onSave={direct.updateCredentials}
      />

      <ConfirmDialog
        title='Retire this courier account?'
        open={Boolean(pendingRemoval)}
        setOpen={(open) => !open && setPendingRemoval(null)}
        onConfirm={async () => {
          const row = pendingRemoval;
          setPendingRemoval(null);
          try {
            const res = await direct.retire(row.accountCode);
            enqueueSnackbar(res?.message || `${row.carrierName} retired.`, { variant: 'success' });
          } catch (e) {
            enqueueSnackbar(e.message, { variant: 'error' });
          }
        }}
        onReject={() => setPendingRemoval(null)}
      >
        <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE }}>
          {pendingRemoval
            ? `${pendingRemoval.carrierName} · ${pendingRemoval.accountCode} will be switched off for allocation permanently. The account is not deleted — shipments, COD remittances and invoices already raised against it still point here, so the record and everything you uploaded for it are kept. This cannot be undone: to use the contract again, connect it as a new account.`
            : ''}
        </Typography>
      </ConfirmDialog>
    </Box>
  );
}

export default withLayout(MyCouriers);
